import Link from 'next/link';
import React from 'react';
import { IoArrowForward, IoSearchSharp, IoTimeOutline } from 'react-icons/io5';

import { Box, Menu, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

import { all, CompanyProfile, Component, ComponentDefinition } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { onlyLooseMatches } from 'helpers/only-loose-matches';
import { TextHelpers } from 'helpers/TextHelpers';

import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { CompanyLogoFallback } from 'components/company-logo/CompanyLogoFallback';
import { ComponentIcon } from 'components/component-icons/ComponentIcon';
import { SEARCH_SUGGESTIONS } from 'components/component-overview/components/SearchSuggestions';
import { SearchService } from 'components/component-overview/services/SearchService';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useComponentMeta } from 'hooks/use-component-meta';
import { useSearchAutosuggest } from 'hooks/use-search-autosuggest';
import { useSearchHistory } from 'hooks/use-search-history';
import { InternalTrackingService } from 'services/InternalTrackingService';

import cx from './SearchBarAutocomplete.module.scss';

const MIN_QUERY_LENGTH = 3;
const MAX_RESULTS = 11;

const SearchBarAutocomplete = ({
    query,
    setQuery,
    handleSearch,
}: {
    query: string;
    setQuery: (query: string) => void;
    handleSearch: (query: string) => void;
}) => {
    const [debouncedQuery] = useDebouncedValue(query, 300);

    const { companies, products, isLoading } = useSearchAutosuggest(debouncedQuery);

    const { searchHistory } = useSearchHistory();
    const cleanHistory = searchHistory.filter(
        (history) => TextHelpers.textMatches(history, query) && !TextHelpers.textMatches(history, query, true),
    );

    const componentTypes =
        query.length >= MIN_QUERY_LENGTH
            ? Object.values(all).filter((component) => TextHelpers.textMatches(component.name, query))
            : [];

    const echoCount = query.length ? 1 : 0;
    const fixedResultCount = echoCount + cleanHistory.length + companies.length + products.length;
    const componentTypeLimit = Math.max(1, MAX_RESULTS - fixedResultCount);
    const visibleComponentTypes = componentTypes.slice(0, componentTypeLimit);

    const children: React.ReactNode[] = [];

    if (query.length) {
        children.push(
            <TextItem label={query} icon={<IoSearchSharp size={16} />} onClick={() => handleSearch(query)} />,
        );
    }

    if (cleanHistory.length) {
        children.push(<History history={cleanHistory} handleSearch={handleSearch} />);
    }

    children.push(
        ...visibleComponentTypes.map((component) => (
            <ComponentType key={component.type} component={component} setQuery={setQuery} />
        )),
        ...(onlyLooseMatches([...products, ...companies])
            ? [<Menu.Label key="no-exact-matches">No exact matches. Closest results:</Menu.Label>]
            : []),
        ...products.map((component) => <Product key={component.id} component={component} />),
        ...companies.map((company) => <Company key={company.id} company={company} setQuery={setQuery} />),
    );

    if (isLoading) {
        children.push(<Menu.Label>Loading…</Menu.Label>);
    }

    if (!children.length) {
        children.push(
            SEARCH_SUGGESTIONS.map((suggestion) => (
                <TextItem
                    key={suggestion}
                    label={suggestion}
                    icon={<IoSearchSharp size={16} />}
                    onClick={() => handleSearch(suggestion)}
                />
            )),
        );
    }

    return (
        <Menu.Dropdown className={cx.dropdown}>
            {/* <TextItem
                label="Switch to AI Mode"
                icon={<TbSparkles size={16} />}
                variant="gradient"
                onClick={() => {
                    SearchPlanHelpers.switchPage('plan');
                }}
            /> */}
            {children}
        </Menu.Dropdown>
    );
};

const History = ({ history, handleSearch }: { history: string[]; handleSearch: (query: string) => void }) => (
    <>
        {history.map((query) => (
            <TextItem
                key={query}
                label={query}
                icon={<IoTimeOutline size={16} />}
                onClick={() => handleSearch(query)}
            />
        ))}
    </>
);

const TextItem = ({
    prefix,
    suffix,
    label,
    icon,
    variant,
    onClick,
}: {
    prefix?: string;
    suffix?: React.ReactNode;
    label: string;
    icon: React.ReactNode;
    variant?: 'default' | 'gradient';
    onClick: () => void;
}) => {
    return (
        <Menu.Item
            leftSection={<Box className={cx.icon}>{icon}</Box>}
            rightSection={suffix}
            onClick={onClick}
            data-variant={variant}
        >
            <Text inherit>
                {prefix && <Text span inherit>{`${prefix} > `}</Text>}
                <Text
                    span
                    inherit
                    fw={variant === 'gradient' ? 700 : 600}
                    className={variant === 'gradient' ? 'gradient-text-default' : ''}
                >
                    {label}
                </Text>
            </Text>
        </Menu.Item>
    );
};

const ComponentType = ({
    component,
    setQuery,
}: {
    component: ComponentDefinition;
    setQuery: (query: string) => void;
}) => {
    const onClick = () => {
        SearchService.resetFilters();
        SearchService.setFilter('type', component.type);

        InternalTrackingService.track('product.search.query', {
            query: component.plural,
            autocomplete: 'products.type',
        });

        setQuery(component.plural);
    };

    return (
        <Menu.Item
            leftSection={
                <Box className={`${cx.icon} ${cx.componentIcon}`}>
                    <ComponentIcon type={component.type} />
                </Box>
            }
            onClick={onClick}
        >
            <Text inherit fw={600}>
                All {component.plural}
            </Text>
        </Menu.Item>
    );
};

const Company = ({ company, setQuery }: { company: CompanyProfile; setQuery: (query: string) => void }) => {
    const locationSummary = CompanyHelpers.summarizeLocations(company.locations);

    const onClick = () => {
        SearchService.resetFilters();
        SearchService.setFilter('manufacturer', company.id);

        InternalTrackingService.track('product.search.query', {
            query: company.name,
            autocomplete: 'products.manufacturer',
        });

        setQuery(company.name);
    };

    return (
        <Menu.Item
            leftSection={
                <Box className={cx.icon}>
                    <CompanyLogo
                        logos={company.logos}
                        width={32}
                        fallback={<CompanyLogoFallback name={company.name} color={company.color} size="sm" />}
                    />
                </Box>
            }
            onClick={onClick}
        >
            <Text inherit fw={600}>
                {company.name}
            </Text>
            {locationSummary && (
                <Text inherit c="dimmed">
                    {locationSummary}
                </Text>
            )}
        </Menu.Item>
    );
};

const Product = ({ component }: { component: Component }) => {
    const { uniqueName, typeName, manufacturerName } = useComponentMeta(component);

    const onClick = () => {
        InternalTrackingService.track('product.search.query', {
            query: uniqueName,
            autocomplete: 'products.link',
        });
    };

    return (
        <Menu.Item
            component={Link}
            href={ComponentHelpers.urls.view(component.id)}
            target="_blank"
            onClick={onClick}
            leftSection={
                <Box className={cx.icon}>
                    <ComponentThumbnail showEmpty component={component} />
                </Box>
            }
            rightSection={<IoArrowForward />}
        >
            <Text inherit fw={600}>
                {uniqueName}
            </Text>
            <Text inherit c="dimmed">
                {[typeName, manufacturerName].filter(Boolean).join(' • ')}
            </Text>
        </Menu.Item>
    );
};

export { SearchBarAutocomplete };
